/**
 * @fileoverview The Sales Pipeline, My Page and Sales Performance cards of the Sales Dashboard.
 * @description Three summary cards that each open their page. Sales Pipeline shows the backlog with
 * a Hot, Warm and Cold bar and the awaiting-approval count (there is no unassigned figure); My Page
 * shows the caller's targets with a bar by status; Sales Performance shows the financial year's
 * achievement with a bar per month, calling out the lowest finished month when the backend sent
 * one. Each card renders only when its section is present.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import { monthBarHeight, myPageSplit } from '@/features/sales/lib/sales-dashboard.utils'
import { monthLabel } from '@/features/sales/lib/sales-performance.utils'
import SummaryCardShell from '@/shared/components/molecules/SummaryCardShell'
import { ROUTES } from '@/shared/constants/routes.constants'

import DashboardLegendStat from './DashboardLegendStat'
import SegmentedBar from './SegmentedBar'

import type {
  DashboardAchievement,
  DashboardBacklog,
  DashboardMonth,
  DashboardMyPage,
} from '@/features/sales/types/sales-dashboard.types'

/** @description The bar colour for a month: the dip, the latest month, or any other. */
const MONTH_TONE_CLASS_NAMES = {
  dip: 'bg-dip',
  latest: 'bg-primary',
  other: 'bg-bar-light',
} as const

interface MonthBarProps {
  month: DashboardMonth
  tone: keyof typeof MONTH_TONE_CLASS_NAMES
}

/**
 * @component
 * @param {MonthBarProps} props - One month of the Sales Performance bars.
 * @param {DashboardMonth} props.month - The month's figures.
 * @param {'dip' | 'latest' | 'other'} props.tone - Which colour the bar takes.
 */
function MonthBar({ month, tone }: MonthBarProps) {
  const { i18n } = useTranslation()

  return (
    <div
      data-testid={`dashboard-performance-month-${month.month}`}
      title={`${monthLabel(month.month, i18n.language)}: ${month.percent ?? 0}%`}
      style={{ height: `${monthBarHeight(month)}%` }}
      className={`flex-1 rounded-t-sm ${MONTH_TONE_CLASS_NAMES[tone]}`}
    />
  )
}

/**
 * @component
 * @param {{ backlog: DashboardBacklog }} props - The backlog section.
 * @param {DashboardBacklog} props.backlog - The Sales Pipeline backlog.
 */
export function PipelineCard({ backlog }: { backlog: DashboardBacklog }) {
  const { t } = useTranslation()

  return (
    <SummaryCardShell
      to={ROUTES.SALES_PIPELINE}
      testId="dashboard-card-pipeline"
      iconBg="bg-info-bg"
      icon={
        <svg
          className="text-primary"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.9"
          strokeLinecap="round"
        >
          <path d="M3.5 5h17l-6.6 7.6V19l-3.8 2v-8.4z" />
        </svg>
      }
      title={t('nav.salesPipeline')}
      value={backlog.total}
      caption={t('sales.dashboard.cards.pipelineCaption')}
      footer={
        <>
          <DashboardLegendStat
            label={t('sales.dashboard.cards.pipelineHot')}
            count={backlog.hot}
            testId="dashboard-pipeline-hot"
            toneClassName="text-danger"
          />
          <DashboardLegendStat
            label={t('sales.dashboard.cards.pipelineWarm')}
            count={backlog.warm}
            testId="dashboard-pipeline-warm"
            toneClassName="text-status-warning"
          />
          <DashboardLegendStat
            label={t('sales.dashboard.cards.pipelineCold')}
            count={backlog.cold}
            testId="dashboard-pipeline-cold"
            toneClassName="text-info"
          />
          <span className="flex-1" />
          <span
            className="text-xs font-semibold text-slate"
            data-testid="dashboard-pipeline-awaiting-approval"
          >
            {t('sales.dashboard.cards.pipelineAwaitingApproval', {
              count: backlog.awaitingApproval,
            })}
          </span>
        </>
      }
    >
      <SegmentedBar
        testId="dashboard-pipeline-bar"
        segments={[
          {
            id: 'hot',
            label: t('sales.dashboard.cards.pipelineHot'),
            value: backlog.hot,
            color: 'bg-danger',
          },
          {
            id: 'warm',
            label: t('sales.dashboard.cards.pipelineWarm'),
            value: backlog.warm,
            color: 'bg-status-warning',
          },
          {
            id: 'cold',
            label: t('sales.dashboard.cards.pipelineCold'),
            value: backlog.cold,
            color: 'bg-cold',
          },
        ]}
      />
    </SummaryCardShell>
  )
}

/**
 * @component
 * @param {{ myPage: DashboardMyPage }} props - The My Page section.
 * @param {DashboardMyPage} props.myPage - The caller's own My Page figures.
 */
export function MyPageCard({ myPage }: { myPage: DashboardMyPage }) {
  const { t } = useTranslation()
  const split = myPageSplit(myPage)

  return (
    <SummaryCardShell
      to={ROUTES.SALES_MY_PAGE}
      testId="dashboard-card-my-page"
      iconBg="bg-info-soft-bg"
      icon={
        <svg
          className="text-accent-panel"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.9"
          strokeLinecap="round"
        >
          <circle cx="10" cy="8" r="3.3" />
          <path d="M4 20c0-3.2 2.6-5.4 5.8-5.4 1.3 0 2.5.3 3.5 1" />
          <path d="M15.4 18.4l1.9 1.9 3.3-3.8" />
        </svg>
      }
      title={t('nav.myPage')}
      value={myPage.total}
      caption={t('sales.dashboard.cards.myPageCaption')}
      footer={
        <>
          <DashboardLegendStat
            label={t('sales.dashboard.cards.myPageDone')}
            count={myPage.achieved}
            testId="dashboard-my-page-done"
            toneClassName="text-success"
          />
          <DashboardLegendStat
            label={t('sales.dashboard.cards.myPageAtRisk')}
            count={myPage.atRisk}
            testId="dashboard-my-page-at-risk"
            toneClassName="text-status-warning"
          />
          <span className="flex-1" />
          {myPage.overdue > 0 && (
            <span
              className="text-xs font-semibold text-danger"
              data-testid="dashboard-my-page-overdue"
            >
              {t('sales.dashboard.cards.myPageOverdue', { count: myPage.overdue })}
            </span>
          )}
        </>
      }
    >
      <SegmentedBar
        testId="dashboard-my-page-bar"
        segments={[
          {
            id: 'awaiting-payment',
            label: t('sales.myWork.displayStatuses.AWAITING_PAYMENT'),
            value: split.awaitingPayment,
            color: 'bg-info',
          },
          {
            id: 'at-risk',
            label: t('sales.dashboard.cards.myPageAtRisk'),
            value: split.atRisk,
            color: 'bg-status-warning',
          },
          {
            id: 'overdue',
            label: t('sales.myWork.displayStatuses.OVERDUE'),
            value: split.overdue,
            color: 'bg-danger',
          },
          {
            id: 'other',
            label: t('sales.dashboard.cards.myPageOther'),
            value: split.other,
            color: 'bg-track',
          },
        ]}
      />
    </SummaryCardShell>
  )
}

/**
 * @component
 * @param {{ achievement: DashboardAchievement }} props - The achievement section.
 * @param {DashboardAchievement} props.achievement - The financial year to date.
 */
export function PerformanceCard({ achievement }: { achievement: DashboardAchievement }) {
  const { t, i18n } = useTranslation()
  const { months, lowestMonth } = achievement
  const first = months[0]
  const last = months.at(-1)

  return (
    <SummaryCardShell
      to={ROUTES.SALES_PERFORMANCE}
      testId="dashboard-card-performance"
      iconBg="bg-success-bg"
      valueColor="text-status-warning"
      icon={
        <svg
          className="text-teal"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.9"
          strokeLinecap="round"
        >
          <path d="M3.5 17l5.5-5.5 3.5 3.5 7.5-7.5" />
          <path d="M15 7.5h5.5V13" />
        </svg>
      }
      title={t('nav.salesPerformance')}
      value={`${achievement.percent}%`}
      caption={t('sales.dashboard.cards.performanceCaption', {
        achieved: achievement.achieved,
        assigned: achievement.assigned,
      })}
      footer={
        <>
          {first && last && (
            <span
              className="text-xs font-medium text-slate"
              data-testid="dashboard-performance-range"
            >
              {monthLabel(first.month, i18n.language)} – {monthLabel(last.month, i18n.language)}
            </span>
          )}
          <span className="flex-1" />
          {lowestMonth && (
            <span
              className="text-xs font-semibold text-danger"
              data-testid="dashboard-performance-dip"
            >
              {t('sales.dashboard.cards.performanceDip', {
                month: monthLabel(lowestMonth.month, i18n.language),
                pct: lowestMonth.percent,
              })}
            </span>
          )}
        </>
      }
    >
      <div className="flex h-10 items-end gap-1.5" data-testid="dashboard-performance-months">
        {months.map((month) => (
          <MonthBar
            key={month.month}
            month={month}
            tone={month.month === lowestMonth?.month ? 'dip' : month === last ? 'latest' : 'other'}
          />
        ))}
      </div>
    </SummaryCardShell>
  )
}
